// components/modals/ReportModals.js
import React from 'react';
import { 
  FileText, 
  Edit2, 
  Trash2, 
  Plus, 
  X,
  Info,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import './ReportModals.css';

// ========================================
// CONSTANTS & CONFIGURATION
// ========================================

const PERIODS = [
  { value: 'auto', label: 'Auto (Recommended)', description: 'Automatically select best period based on delivery frequency' },
  { value: 'yesterday', label: 'Yesterday', description: 'Data from the previous day' },
  { value: 'last_7_days', label: 'Last 7 Days', description: 'Data from the past week' },
  { value: 'last_30_days', label: 'Last 30 Days', description: 'Data from the past month' },
  { value: 'last_90_days', label: 'Last 90 Days', description: 'Data from the past quarter' },
  { value: 'this_week', label: 'This Week', description: 'Data from Monday to now' },
  { value: 'this_month', label: 'This Month', description: 'Data from start of month to now' },
  { value: 'last_month', label: 'Last Month', description: 'Data from the previous complete month' },
  { value: 'this_quarter', label: 'This Quarter', description: 'Data from start of quarter to now' },
  { value: 'last_quarter', label: 'Last Quarter', description: 'Data from the previous complete quarter' },
  { value: 'this_year', label: 'This Year', description: 'Data from start of year to now' },
  { value: 'last_year', label: 'Last Year', description: 'Data from the previous complete year' }
];

const PERIOD_RECOMMENDATIONS = {
  daily: 'yesterday',
  weekly: 'last_7_days',
  monthly: 'last_month',
  quarterly: 'last_quarter'
};

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

// ========================================
// UTILITY FUNCTIONS
// ========================================

const getRecommendedPeriod = (frequency) => {
  return PERIOD_RECOMMENDATIONS[frequency] || 'last_30_days';
};

const isPeriodOptimal = (period, frequency) => {
  if (period === 'auto') return true;
  return period === getRecommendedPeriod(frequency);
};

const getPeriodInfo = (value) => {
  return PERIODS.find(p => p.value === value);
};

// ========================================
// BASE COMPONENTS
// ========================================

const ModalWrapper = ({ show, onClose, title, icon, children, size = "lg" }) => {
  if (!show) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-container modal-${size}`}>
        <div className="modal-header">
          <h3 className="modal-title">
            {icon}
            <span>{title}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-button"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const NotificationBanner = ({ type, icon, title, children, action }) => {
  return (
    <div className={`notification-banner notification-${type}`}>
      {icon && <div className="notification-icon">{icon}</div>}
      <div className="notification-content">
        {title && <div className="notification-title">{title}</div>}
        <div>{children}</div>
      </div>
      {action && action}
    </div>
  );
};

const FormField = ({ label, required, help, children, fullWidth }) => {
  return (
    <div className={`form-field ${fullWidth ? 'form-field-full' : ''}`}>
      {label && (
        <label className={`form-label ${required ? 'form-label-required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {help && <p className="form-help">{help}</p>}
    </div>
  );
};

// ========================================
// PERIOD SELECTOR COMPONENT
// ========================================

const PeriodSelector = ({ reportForm, onPeriodChange }) => {
  const currentPeriod = reportForm?.date_range_config?.period || 'auto';
  const frequency = reportForm?.frequency || 'weekly';
  const recommendedPeriod = getRecommendedPeriod(frequency);
  const isOptimal = isPeriodOptimal(currentPeriod, frequency);
  const currentPeriodInfo = getPeriodInfo(currentPeriod);

  const renderRecommendation = () => {
    if (currentPeriod === 'auto') {
      const recommendedLabel = getPeriodInfo(recommendedPeriod)?.label;
      return (
        <NotificationBanner type="success" icon={<CheckCircle size={20} />} title="Auto-optimized">
          Will select "{recommendedLabel}" for {frequency} reports
        </NotificationBanner>
      );
    }

    if (isOptimal) {
      return (
        <NotificationBanner type="success" icon={<CheckCircle size={20} />} title="Perfect match">
          Optimal choice for {frequency} reports
        </NotificationBanner>
      );
    }

    const recommendedLabel = getPeriodInfo(recommendedPeriod)?.label;
    return (
      <NotificationBanner 
        type="warning" 
        icon={<AlertTriangle size={20} />} 
        title="Suggestion"
        action={
          <button 
            type="button" 
            className="notification-action"
            onClick={() => onPeriodChange(recommendedPeriod)}
          >
            Apply
          </button>
        }
      >
        Consider "{recommendedLabel}" for {frequency} reports
      </NotificationBanner>
    );
  };

  return (
    <div>
      <FormField 
        label="Report Data Period" 
        help={currentPeriodInfo?.description}
      >
        <select
          value={currentPeriod}
          onChange={(e) => onPeriodChange(e.target.value)}
          className="form-input"
        >
          {PERIODS.map(period => (
            <option key={period.value} value={period.value}>
              {period.label}
            </option>
          ))}
        </select>
      </FormField>

      {renderRecommendation()}
    </div>
  );
};

// ========================================
// RECIPIENTS COMPONENT
// ========================================

const RecipientsManager = ({ recipients = [''], addRecipient, removeRecipient, updateRecipient }) => {
  return (
    <div className="recipients-container">
      {recipients.map((recipient, index) => (
        <div key={index} className="recipient-item">
          <input
            type="email"
            value={recipient}
            onChange={(e) => updateRecipient(index, e.target.value)}
            className="form-input recipient-input"
            placeholder="email@example.com"
            required
          />
          {recipients.length > 1 && (
            <button
              type="button"
              onClick={() => removeRecipient(index)}
              className="recipient-remove"
              aria-label="Remove recipient"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ))}
      
      <button
        type="button"
        onClick={addRecipient}
        className="add-recipient-button"
      >
        <Plus size={16} />
        Add Another Recipient
      </button>
    </div>
  );
};

// ========================================
// SCHEDULE CONFIGURATION COMPONENT
// ========================================

const ScheduleConfig = ({ frequency, frequencyConfig, updateFrequencyConfig }) => {
  return (
    <>
      {frequency === 'weekly' && (
        <FormField label="Day of Week">
          <select
            value={frequencyConfig?.day_of_week || 1}
            onChange={(e) => updateFrequencyConfig('day_of_week', parseInt(e.target.value))}
            className="form-input"
          >
            {DAYS_OF_WEEK.map(day => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </FormField>
      )}
      
      {(frequency === 'monthly' || frequency === 'quarterly') && (
        <FormField label="Day of Month">
          <input
            type="number"
            min="1"
            max="31"
            value={frequencyConfig?.day_of_month || 1}
            onChange={(e) => updateFrequencyConfig('day_of_month', parseInt(e.target.value))}
            className="form-input"
          />
        </FormField>
      )}

      <FormField label="Delivery Time">
        <input
          type="time"
          value={frequencyConfig?.time || '09:00'}
          onChange={(e) => updateFrequencyConfig('time', e.target.value)}
          className="form-input"
        />
      </FormField>
    </>
  );
};

// ========================================
// MAIN FORM FIELDS COMPONENT
// ========================================

const ReportFormFields = ({ 
  reportForm, 
  updateReportForm, 
  updateFrequencyConfig, 
  addRecipient, 
  removeRecipient, 
  updateRecipient,
  onFrequencyChange,
  onPeriodChange,
  actionButtons
}) => {
  const formData = {
    name: '',
    frequency: 'weekly',
    frequency_config: { time: '09:00' },
    format_settings: { format: 'pdf' },
    recipients: [''],
    date_range_config: { period: 'auto' },
    ...reportForm
  };

  const handleFrequencyChange = (e) => {
    const newFrequency = e.target.value;
    updateReportForm('frequency', newFrequency);
    if (onFrequencyChange) {
      onFrequencyChange(newFrequency);
    }
  };

  return (
    <div className="modal-content modal-content-reporting">
      {/* Basic Information */}
      <section className="form-section">
        <h4 className="form-section-header">Basic Information</h4>
        
        <div className="form-grid form-grid-2">
          <FormField 
            label="Report Name" 
            required 
            fullWidth
            help="A descriptive name to identify this report"
          >
            <input
              type="text"
              value={formData.name}
              onChange={(e) => updateReportForm('name', e.target.value)}
              className="form-input"
              placeholder="e.g., Weekly Sales Report"
              required
            />
          </FormField>

          <FormField label="Output Format">
            <select
              value={formData.format_settings?.format || 'pdf'}
              onChange={(e) => updateReportForm('format_settings', { 
                ...formData.format_settings, 
                format: e.target.value 
              })}
              className="form-input"
            >
              <option value="pdf">PDF Document</option>
              <option value="csv">CSV Data</option>
            </select>
          </FormField>
        </div>
      </section>

      {/* Schedule Configuration */}
      <section className="form-section">
        <h4 className="form-section-header">Schedule Configuration</h4>
        
        <div className="form-grid form-grid-schedule">
          <FormField label="Frequency" required>
            <select
              value={formData.frequency}
              onChange={handleFrequencyChange}
              className="form-input"
              required
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </FormField>

          <ScheduleConfig
            frequency={formData.frequency}
            frequencyConfig={formData.frequency_config}
            updateFrequencyConfig={updateFrequencyConfig}
          />
        </div>
      </section>

      {/* Data Period */}
      <section className="form-section">
        <h4 className="form-section-header">Data Configuration</h4>
        <PeriodSelector
          reportForm={formData}
          onPeriodChange={onPeriodChange}
        />
      </section>

      {/* Recipients */}
      <section className="form-section">
        <h4 className="form-section-header">
          Recipients <span className="text-required">*</span>
        </h4>
        <RecipientsManager
          recipients={formData.recipients}
          addRecipient={addRecipient}
          removeRecipient={removeRecipient}
          updateRecipient={updateRecipient}
        />
      </section>

      {/* Action Buttons */}
      {actionButtons && (
        <div className="form-actions">
          {actionButtons}
        </div>
      )}
    </div>
  );
};

// ========================================
// CREATE REPORT MODAL
// ========================================

export const CreateReportModal = ({ 
  show, 
  onClose, 
  onSubmit, 
  reportForm, 
  updateReportForm, 
  updateFrequencyConfig, 
  addRecipient, 
  removeRecipient, 
  updateRecipient,
  onFrequencyChange,
  onPeriodChange,
  loading 
}) => {
  const handlePeriodChange = (newPeriod) => {
    updateReportForm('date_range_config', { 
      ...reportForm?.date_range_config,
      period: newPeriod 
    });
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  const actionButtons = (
    <div className="modal-actions">
      <button
        type="button"
        onClick={onClose}
        className="modal-button modal-button-outline"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={loading}
        className="modal-button modal-button-primary"
      >
        {loading ? (
          <>
            <div className="spinner" />
            Creating...
          </>
        ) : (
          <>
            <FileText size={14} />
            Create Report
          </>
        )}
      </button>
    </div>
  );

  return (
    <ModalWrapper
      show={show}
      onClose={onClose}
      title="Create New Report"
      icon={<FileText size={20} />}
      size="xl"
    >
      <form onSubmit={onSubmit}>
        <ReportFormFields
          reportForm={reportForm}
          updateReportForm={updateReportForm}
          updateFrequencyConfig={updateFrequencyConfig}
          addRecipient={addRecipient}
          removeRecipient={removeRecipient}
          updateRecipient={updateRecipient}
          onFrequencyChange={onFrequencyChange}
          onPeriodChange={handlePeriodChange}
          actionButtons={actionButtons}
        />
      </form>
    </ModalWrapper>
  );
};

// ========================================
// EDIT REPORT MODAL
// ========================================

export const EditReportModal = ({ 
  show, 
  onClose, 
  onSubmit, 
  selectedReport, 
  reportForm, 
  updateReportForm, 
  updateFrequencyConfig, 
  addRecipient, 
  removeRecipient, 
  updateRecipient,
  onFrequencyChange,
  onPeriodChange,
  loading 
}) => {
  const handlePeriodChange = (newPeriod) => {
    updateReportForm('date_range_config', { 
      ...reportForm?.date_range_config,
      period: newPeriod 
    });
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };

  const actionButtons = (
    <div className="modal-actions">
      <button
        type="button"
        onClick={onClose}
        className="modal-button modal-button-outline"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={loading}
        className="modal-button modal-button-primary"
      >
        {loading ? (
          <>
            <div className="spinner" />
            Updating...
          </>
        ) : (
          <>
            <Edit2 size={14} />
            Update Report
          </>
        )}
      </button>
    </div>
  );

  return (
    <ModalWrapper
      show={show}
      onClose={onClose}
      title={`Edit: ${selectedReport?.name || 'Untitled'}`}
      icon={<Edit2 size={20} />}
      size="xl"
    >
      <form onSubmit={onSubmit}>
        <div className="modal-content-compact">
          <NotificationBanner type="info" icon={<Info size={20} />} title="Current Status">
            <div className={`status-badge status-${selectedReport?.status || 'unknown'}`}>
              {selectedReport?.status || 'Unknown'}
            </div>
          </NotificationBanner>
        </div>

        <ReportFormFields
          reportForm={reportForm}
          updateReportForm={updateReportForm}
          updateFrequencyConfig={updateFrequencyConfig}
          addRecipient={addRecipient}
          removeRecipient={removeRecipient}
          updateRecipient={updateRecipient}
          onFrequencyChange={onFrequencyChange}
          onPeriodChange={handlePeriodChange}
          actionButtons={actionButtons}
        />
      </form>
    </ModalWrapper>
  );
};

// ========================================
// DELETE CONFIRMATION MODAL
// ========================================

export const DeleteConfirmModal = ({ 
  show, 
  onClose, 
  onConfirm, 
  selectedReport, 
  formatSchedule 
}) => {
  return (
    <ModalWrapper
      show={show}
      onClose={onClose}
      title="Delete Report"
      icon={<Trash2 size={20} className="text-danger" />}
      size="md"
    >
      <div className="modal-content">
        <div className="delete-warning">
          <div className="delete-warning-header">
            <AlertTriangle className="delete-warning-icon" />
            <p className="delete-warning-title">This action cannot be undone</p>
          </div>
          <p className="delete-warning-text">
            The report will be permanently deleted and recipients will no longer receive it.
          </p>
        </div>
        
        <div className="report-details-card">
          <div className="report-name">
            {selectedReport?.name || 'Untitled Report'}
          </div>
          <div className="report-meta-tags">
            <span className="report-meta-tag">
              {selectedReport?.report_type || 'custom'}
            </span>
            <span className="report-meta-tag">
              {formatSchedule ? 
                formatSchedule(selectedReport?.frequency, selectedReport?.frequency_config) : 
                'Unknown schedule'
              }
            </span>
          </div>
          <p className="report-recipients-text">
            {selectedReport?.recipient_count || 0} recipient(s) affected
          </p>
        </div>

        <div className="form-actions">
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="modal-button modal-button-outline"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="modal-button modal-button-danger"
            >
              <Trash2 size={14} />
              Delete Report
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};